import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const AppContext = createContext(null);

function readStored(key, fallback = '') {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

export function AppProvider({ children, product }) {
  const prefix = product?.id || 'ride';
  const environments = product?.environments || [];
  const defaultEnvId = environments.find(e => e.id !== 'custom')?.id || environments[0]?.id || 'staging';

  const [envId, setEnvId] = useState(() => {
    const stored = readStored(`envId_${prefix}`, defaultEnvId);
    return environments.some(e => e.id === stored) ? stored : defaultEnvId;
  });
  const [customUrl, setCustomUrl] = useState(() => readStored(`customUrl_${prefix}`, ''));
  const [savedToken, setSavedToken] = useState(() => readStored(`savedToken_${prefix}`, ''));
  const [savedBearerKey, setSavedBearerKey] = useState(() => readStored(`savedBearerKey_${prefix}`, ''));

  useEffect(() => { localStorage.setItem(`envId_${prefix}`, envId); }, [envId, prefix]);
  useEffect(() => { localStorage.setItem(`customUrl_${prefix}`, customUrl); }, [customUrl, prefix]);
  useEffect(() => { localStorage.setItem(`savedToken_${prefix}`, savedToken); }, [savedToken, prefix]);
  useEffect(() => { localStorage.setItem(`savedBearerKey_${prefix}`, savedBearerKey); }, [savedBearerKey, prefix]);

  const currentEnv = environments.find(e => e.id === envId) || environments[0] || { id: 'custom', label: 'Custom', url: '', color: '#6366f1' };
  const baseUrl = (envId === 'custom' ? customUrl : currentEnv.url).replace(/\/$/, '');

  const value = useMemo(() => ({
    product,
    environments,
    envId,
    setEnvId,
    customUrl,
    setCustomUrl,
    currentEnv: envId === 'custom' ? { ...currentEnv, url: customUrl } : currentEnv,
    baseUrl,
    savedToken,
    setSavedToken,
    savedBearerKey,
    setSavedBearerKey,
  }), [product, environments, envId, customUrl, currentEnv, baseUrl, savedToken, savedBearerKey]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
